import { useMemo } from 'react';
import { T, toLocalID } from 'src/shared/helpers';
import type { ToolCategory, ToolDefinition, RuleField, SidePanelProduct, ProductToolData } from './types';

export const useToolCategoryLabels = () =>
    useMemo<Record<ToolCategory, string>>(
        () => ({
            'product-setup': T._('Core Tools', 'Admin'),
            'orders-output': T._('Orders & Output', 'Admin'),
            ai: T._('AI Creative Tools', 'Admin'),
            'tools-settings': T._('Tools & Settings', 'Admin')
        }),
        []
    );

export const useToolDefinitions = () =>
    useMemo<ToolDefinition[]>(
        () => [
            // Product Setup
            {
                key: 'printing-methods',
                name: T._('Printing methods', 'Admin'),
                description: T._('Define print types, output and customization tools', 'Admin'),
                route: '/printing-methods',
                detailRoute: '/printing-method',
                newRoute: '/printing-method/',
                environments: ['CUSTOMIZER'],
                category: 'product-setup',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016836099-Printing-Methods'
            },
            {
                key: '3d-preview',
                name: T._('3D preview and AR', 'Admin'),
                description: T._('Real-time 3D view and Augmented Reality', 'Admin'),
                route: '/preview3d/products',
                buildSetupRoute: (pid) => `/preview3d/?product_id=${pid}`,
                buildEmbeddedUrl: (_itemId, pid) => `/preview3d/?product_id=${pid}&embedded=true`,
                environments: ['CUSTOMIZER', 'VIEWER'],
                category: 'product-setup',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360011939619-3D-Preview-and-Augmented-Reality'
            },
            {
                key: 'templates',
                name: T._('Pre-designed templates', 'Admin'),
                description: T._('Ready-made designs with editable elements', 'Admin'),
                route: '/tools/template-product-selection/templates',
                environments: ['CUSTOMIZER'],
                category: 'product-setup',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016320114-Pre-designed-Templates'
            },
            {
                key: 'pricing',
                name: T._('Pricing rules', 'Admin'),
                description: T._('Markup prices for customizable products', 'Admin'),
                route: '/pricings',
                environments: ['CUSTOMIZER', 'COMPOSER'],
                category: 'product-setup',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360015984779-How-to-create-pricing-rules',
                customizerTool: true
            },
            {
                key: 'preset-configurations',
                name: T._('Pre-set configurations', 'Admin'),
                description: T._('Predefined compositions of attributes and options', 'Admin'),
                route: '/preset-configurations',
                buildSetupRoute: (pid) => `/preset-configurations/products/${pid}`,
                buildEmbeddedUrl: (_itemId, pid) => `/preset-configurations/products/${pid}?embedded=true`,
                environments: ['COMPOSER'],
                category: 'product-setup',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/8786738011036-Pre-set-Configurations'
            },

            // Orders & Output
            {
                key: 'placement',
                name: T._('Advanced print-ready files builder', 'Admin'),
                description: T._('Nested designs, QR codes, dynamic info', 'Admin'),
                route: '/tools/placement-product-selection',
                buildSetupRoute: (pid) => `/tools/placements/${pid}`,
                buildEmbeddedUrl: (_itemId, pid) => `/tools/placements/${pid}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'orders-output',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360016291573-Orders-and-Print-Ready-Files'
            },
            {
                key: 'moq',
                name: T._('Minimum Order Quantity', 'Admin'),
                description: T._('Min quantity rules per product or variant', 'Admin'),
                route: '/tools/moq-rules',
                detailRoute: '/tools/moq-rule',
                newRoute: '/tools/moq-rule/',
                buildEmbeddedUrl: (itemId) => `/tools/moq-rule/${toLocalID(itemId)}?embedded=true`,
                environments: ['CUSTOMIZER', 'COMPOSER'],
                category: 'orders-output',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/26249997300892-Minimum-Order-Quantity'
            },
            {
                key: 'bulk-variations',
                name: T._('Bulk Variation Forms', 'Admin'),
                description: T._('Multiple variants in one customization', 'Admin'),
                route: '/Settings/Bulk-Variation-Product-List',
                buildSetupRoute: (pid) => `/settings/bulk-variation/${toLocalID(pid)}`,
                buildEmbeddedUrl: (_itemId, pid) => `/settings/bulk-variation/${toLocalID(pid)}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'orders-output',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360012930340-Bulk-Variation-Forms'
            },
            {
                key: 'get-a-quote',
                name: T._('Get a quote', 'Admin'),
                description: T._('Add a quote request button to customizer', 'Admin'),
                route: '/tools/get-a-quote',
                detailRoute: '/tools/get-a-quote/quote-rule',
                newRoute: '/tools/get-a-quote/quote-rule/',
                buildEmbeddedUrl: (itemId) => `/tools/get-a-quote/quote-rule/${toLocalID(itemId)}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'orders-output',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360015664999--Get-a-quote-tool'
            },
            {
                key: 'extra-options',
                name: T._('Additional Custom Properties', 'Admin'),
                description: T._('Additional attributes not shown on the product', 'Admin'),
                route: '/tools/extra-options',
                detailRoute: '/tools/extra-option',
                newRoute: '/tools/extra-option/',
                buildEmbeddedUrl: (itemId) => `/tools/extra-option/${itemId}?embedded=true`,
                environments: ['CUSTOMIZER', 'COMPOSER'],
                category: 'orders-output',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360017548099-Dynamic-product-s-properties'
            },

            // AI Creative Tools
            {
                key: 'ai-image-generation',
                name: T._('AI Image Generator & Editor', 'Admin'),
                description: T._('Generate and edit images with AI', 'Admin'),
                route: '/ai/image-generation-editing',
                environments: ['CUSTOMIZER'],
                category: 'ai',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/23868482378524'
            },
            {
                key: 'ai-design-generation',
                name: T._('AI Design Generator', 'Admin'),
                description: T._('Generate complete designs with AI', 'Admin'),
                route: '/ai/design-generation',
                environments: ['CUSTOMIZER'],
                category: 'ai',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/28834399497244'
            },
            {
                key: 'ai-lifestyle-scenes',
                name: T._('AI Lifestyle Scenes', 'Admin'),
                description: T._('Generate lifestyle product staging with AI', 'Admin'),
                route: '/ai/product-staging-rules',
                detailRoute: '/ai/product-staging-rule',
                newRoute: '/ai/product-staging-rule/',
                buildEmbeddedUrl: (itemId) => `/ai/product-staging-rule/${toLocalID(itemId)}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'ai'
            },
            {
                key: 'ai-try-on',
                name: T._('AI Try-on', 'Admin'),
                description: T._('Virtual model try-on powered by AI', 'Admin'),
                route: '/ai/virtual-model-rules',
                detailRoute: '/ai/virtual-model-rule',
                newRoute: '/ai/virtual-model-rule/',
                buildEmbeddedUrl: (itemId) => `/ai/virtual-model-rule/${toLocalID(itemId)}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'ai',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/14416061856796-Virtual-Try-On'
            },

            // Tools & Settings
            {
                key: 'images',
                name: T._('Images and clipart galleries', 'Admin'),
                description: T._('Manage galleries of images and clipart', 'Admin'),
                route: '/tools/images',
                buildEmbeddedUrl: () => `/tools/images?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'tools-settings',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360007632300-Images-and-Clipart-Gallery'
            },
            {
                key: 'shapes',
                name: T._('Shapes and masks', 'Admin'),
                description: T._('Shapes for decoration or image masking', 'Admin'),
                route: '/tools/shapes',
                environments: ['CUSTOMIZER'],
                category: 'tools-settings',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360020531379-Shapes-and-Masks'
            },
            {
                key: 'collage',
                name: T._('Collage layouts', 'Admin'),
                description: T._('Let customers upload images in collage grids', 'Admin'),
                route: '/tools/collage-layout',
                detailRoute: '/tools/collage-layout/grid-rule',
                newRoute: '/tools/collage-layout/grid-rule/',
                buildEmbeddedUrl: (itemId) => `/tools/collage-layout/grid-rule/${toLocalID(itemId)}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'tools-settings',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360010305840-Collage-Layouts-Tool'
            },
            {
                key: 'area-background',
                name: T._('Product background editing', 'Admin'),
                description: T._('Colors and images to fill print areas', 'Admin'),
                route: '/tools/area-background',
                detailRoute: '/tools/area-background/rule',
                newRoute: '/tools/area-background/rule/',
                buildEmbeddedUrl: (itemId) => `/tools/area-background/rule/${itemId}?embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'tools-settings',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360025012633-Product-background-editing-tool'
            },
            {
                key: 'profanity',
                name: T._('Profanity check and image moderator', 'Admin'),
                description: T._('Block offensive words and moderate images', 'Admin'),
                route: '/tools/profanity-filter-menu',
                newRoute: '/tools/profanity-filter-rule',
                buildEmbeddedUrl: (itemId) => `/tools/profanity-filter-rule?rule_id=${itemId}&embedded=true`,
                environments: ['CUSTOMIZER'],
                category: 'tools-settings',
                helpUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/4423286336284-Profanity-Filter-and-Image-Moderator'
            }
        ],
        []
    );

export const TOOL_CATEGORY_ORDER: ToolCategory[] = ['product-setup', 'tools-settings', 'ai', 'orders-output'];

export const useToolEditorConfigs = () =>
    useMemo<Record<string, { subtitle: string; fields: RuleField[] }>>(
        () => ({
            templates: {
                subtitle: T._('Pre-designed template', 'Admin'),
                fields: [
                    {
                        key: 'adaptToVariants',
                        label: T._('Adapt items to variants', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('General', 'Admin')
                    },
                    { key: 'isDefault', label: T._('Set as default template', 'Admin'), type: 'switch', defaultValue: false, section: T._('General', 'Admin') }
                ]
            },
            images: {
                subtitle: T._('Images and clipart', 'Admin'),
                fields: [
                    { key: 'allowUpload', label: T._('Allow user image upload', 'Admin'), type: 'switch', defaultValue: true, section: T._('Upload', 'Admin') },
                    { key: 'maxFileSize', label: T._('Max file size (MB)', 'Admin'), type: 'number', defaultValue: '10', section: T._('Upload', 'Admin') },
                    { key: 'allowSvg', label: T._('Allow SVG upload', 'Admin'), type: 'switch', defaultValue: true, section: T._('Formats', 'Admin') },
                    { key: 'allowPdf', label: T._('Allow PDF upload', 'Admin'), type: 'switch', defaultValue: false, section: T._('Formats', 'Admin') },
                    {
                        key: 'canChangeColors',
                        label: T._('Allow SVG color change', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Editing', 'Admin')
                    },
                    { key: 'canUseFilters', label: T._('Allow image filters', 'Admin'), type: 'switch', defaultValue: true, section: T._('Editing', 'Admin') }
                ]
            },
            shapes: {
                subtitle: T._('Shape rule', 'Admin'),
                fields: [
                    {
                        key: 'applyToAll',
                        label: T._('Apply to all print types', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Assignment', 'Admin')
                    },
                    { key: 'allowResize', label: T._('Allow shape resize', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
                    { key: 'allowRotate', label: T._('Allow shape rotation', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
                    { key: 'useMask', label: T._('Use as image mask', 'Admin'), type: 'switch', defaultValue: false, section: T._('Options', 'Admin') }
                ]
            },
            collage: {
                subtitle: T._('Collage layout rule', 'Admin'),
                fields: [
                    { key: 'minImages', label: T._('Min images', 'Admin'), type: 'number', defaultValue: '2', section: T._('Grid', 'Admin') },
                    { key: 'maxImages', label: T._('Max images', 'Admin'), type: 'number', defaultValue: '6', section: T._('Grid', 'Admin') },
                    { key: 'allowShuffle', label: T._('Allow shuffle', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
                    { key: 'allowCustom', label: T._('Allow custom layout', 'Admin'), type: 'switch', defaultValue: false, section: T._('Options', 'Admin') }
                ]
            },
            'area-background': {
                subtitle: T._('Background rule', 'Admin'),
                fields: [
                    { key: 'allowColors', label: T._('Allow solid colors', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
                    {
                        key: 'allowImages',
                        label: T._('Allow background images', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Options', 'Admin')
                    },
                    { key: 'allowUpload', label: T._('Allow image upload', 'Admin'), type: 'switch', defaultValue: false, section: T._('Options', 'Admin') },
                    {
                        key: 'applyToAll',
                        label: T._('Apply to all print types', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Assignment', 'Admin')
                    }
                ]
            },
            placement: {
                subtitle: T._('Placement rule', 'Admin'),
                fields: [
                    {
                        key: 'nestedDesigns',
                        label: T._('Enable nested designs', 'Admin'),
                        type: 'switch',
                        defaultValue: false,
                        section: T._('Features', 'Admin')
                    },
                    { key: 'qrCodes', label: T._('Enable QR codes', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') },
                    { key: 'dynamicInfo', label: T._('Enable dynamic info', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') },
                    { key: 'barcodes', label: T._('Enable barcodes', 'Admin'), type: 'switch', defaultValue: false, section: T._('Features', 'Admin') }
                ]
            },
            '3d-preview': {
                subtitle: T._('3D preview', 'Admin'),
                fields: [
                    { key: 'enableAR', label: T._('Enable Augmented Reality', 'Admin'), type: 'switch', defaultValue: true, section: T._('Features', 'Admin') },
                    { key: 'autoRotate', label: T._('Auto-rotate model', 'Admin'), type: 'switch', defaultValue: false, section: T._('Display', 'Admin') },
                    { key: 'showEnvironment', label: T._('Show environment', 'Admin'), type: 'switch', defaultValue: true, section: T._('Display', 'Admin') }
                ]
            },
            profanity: {
                subtitle: T._('Profanity filter rule', 'Admin'),
                fields: [
                    { key: 'blockText', label: T._('Block offensive text', 'Admin'), type: 'switch', defaultValue: true, section: T._('Text', 'Admin') },
                    { key: 'blockImages', label: T._('Block offensive images', 'Admin'), type: 'switch', defaultValue: true, section: T._('Images', 'Admin') },
                    {
                        key: 'sensitivity',
                        label: T._('Sensitivity', 'Admin'),
                        type: 'select',
                        defaultValue: 'MEDIUM',
                        section: T._('General', 'Admin'),
                        options: [
                            { value: 'LOW', label: T._('Low', 'Admin') },
                            { value: 'MEDIUM', label: T._('Medium', 'Admin') },
                            { value: 'HIGH', label: T._('High', 'Admin') }
                        ]
                    },
                    {
                        key: 'applyToAll',
                        label: T._('Apply to all print types', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Assignment', 'Admin')
                    }
                ]
            },
            'ai-image-generation': {
                subtitle: T._('AI Image Generator', 'Admin'),
                fields: [
                    { key: 'enabled', label: T._('Enable AI image generation', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
                    {
                        key: 'allowCustomPrompt',
                        label: T._('Allow custom prompts', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Options', 'Admin')
                    },
                    {
                        key: 'maxGenerations',
                        label: T._('Max generations per session', 'Admin'),
                        type: 'number',
                        defaultValue: '10',
                        section: T._('Limits', 'Admin')
                    }
                ]
            },
            'ai-design-generation': {
                subtitle: T._('AI Design Generator', 'Admin'),
                fields: [
                    {
                        key: 'enabled',
                        label: T._('Enable AI design generation', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('General', 'Admin')
                    },
                    {
                        key: 'allowCustomPrompt',
                        label: T._('Allow custom prompts', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Options', 'Admin')
                    },
                    {
                        key: 'autoApply',
                        label: T._('Auto-apply generated design', 'Admin'),
                        type: 'switch',
                        defaultValue: false,
                        section: T._('Options', 'Admin')
                    }
                ]
            },
            'ai-lifestyle-scenes': {
                subtitle: T._('AI Lifestyle Scene rule', 'Admin'),
                fields: [
                    {
                        key: 'allowCustomBackground',
                        label: T._('Allow custom background', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('General', 'Admin')
                    },
                    { key: 'numberOfImages', label: T._('Number of images', 'Admin'), type: 'number', defaultValue: '4', section: T._('General', 'Admin') },
                    {
                        key: 'applyToAll',
                        label: T._('Apply to all products', 'Admin'),
                        type: 'switch',
                        defaultValue: false,
                        section: T._('Assignment', 'Admin')
                    }
                ]
            },
            'ai-try-on': {
                subtitle: T._('AI Try-on rule', 'Admin'),
                fields: [
                    { key: 'autoFit', label: T._('Auto-fit to model', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
                    { key: 'allowPoseChange', label: T._('Allow pose change', 'Admin'), type: 'switch', defaultValue: true, section: T._('Options', 'Admin') },
                    {
                        key: 'applyToAll',
                        label: T._('Apply to all products', 'Admin'),
                        type: 'switch',
                        defaultValue: false,
                        section: T._('Assignment', 'Admin')
                    }
                ]
            },
            moq: {
                subtitle: T._('MOQ rule', 'Admin'),
                fields: [
                    { key: 'minimumQuantity', label: T._('Minimum order quantity', 'Admin'), type: 'number', defaultValue: '1' },
                    {
                        key: 'stepType',
                        label: T._('Step type', 'Admin'),
                        type: 'select',
                        defaultValue: 'FIXED',
                        options: [
                            { value: 'FIXED', label: T._('Fixed steps', 'Admin') },
                            { value: 'CUSTOM', label: T._('Custom tiers', 'Admin') }
                        ]
                    },
                    {
                        key: 'fixedStep',
                        label: T._('Fixed step value', 'Admin'),
                        type: 'number',
                        defaultValue: '1',
                        dependsOn: 'stepType:FIXED'
                    },
                    {
                        key: 'customTiers',
                        label: T._('Custom tiers (comma-separated)', 'Admin'),
                        type: 'text',
                        defaultValue: '',
                        dependsOn: 'stepType:CUSTOM'
                    }
                ]
            },
            'bulk-variations': {
                subtitle: T._('Bulk variation form', 'Admin'),
                fields: [
                    { key: 'enabled', label: T._('Enable bulk variations', 'Admin'), type: 'switch', defaultValue: true, section: T._('General', 'Admin') },
                    {
                        key: 'allowMixVariants',
                        label: T._('Allow mixed variants', 'Admin'),
                        type: 'switch',
                        defaultValue: true,
                        section: T._('Options', 'Admin')
                    }
                ]
            },
            'get-a-quote': {
                subtitle: T._('Quote rule', 'Admin'),
                fields: [
                    { key: 'showButton', label: T._('Show quote button', 'Admin'), type: 'switch', defaultValue: true, section: T._('Display', 'Admin') },
                    { key: 'requireEmail', label: T._('Require email', 'Admin'), type: 'switch', defaultValue: true, section: T._('Fields', 'Admin') },
                    { key: 'requirePhone', label: T._('Require phone', 'Admin'), type: 'switch', defaultValue: false, section: T._('Fields', 'Admin') },
                    { key: 'requireMessage', label: T._('Require message', 'Admin'), type: 'switch', defaultValue: false, section: T._('Fields', 'Admin') }
                ]
            }
        }),
        []
    );

export const useProfanityLangs = () =>
    useMemo(
        () => [
            { code: 'en', label: T._('English', 'Admin') },
            { code: 'ar', label: T._('Arabic', 'Admin') },
            { code: 'fr', label: T._('French', 'Admin') },
            { code: 'de', label: T._('German', 'Admin') },
            { code: 'hi', label: T._('Hindi', 'Admin') },
            { code: 'ja', label: T._('Japanese', 'Admin') },
            { code: 'it', label: T._('Italian', 'Admin') },
            { code: 'pt', label: T._('Portuguese', 'Admin') },
            { code: 'ru', label: T._('Russian', 'Admin') },
            { code: 'es', label: T._('Spanish', 'Admin') },
            { code: 'th', label: T._('Thai', 'Admin') },
            { code: 'tr', label: T._('Turkish', 'Admin') }
        ],
        []
    );

export const useProfanityCategories = () =>
    useMemo(
        () => [
            {
                name: 'nudity',
                label: T._('Nudity', 'Admin'),
                subcategories: [
                    { name: 'nudity', id: 1, defaultValue: 92 },
                    { name: 'nuditypartial', id: 9, defaultValue: 95 },
                    { name: 'nuditysafe', id: 10, defaultValue: 90 }
                ]
            },
            {
                name: 'wad',
                label: T._('WAD', 'Admin'),
                subcategories: [
                    { name: 'weapon', id: 2, defaultValue: 90 },
                    { name: 'alcohol', id: 11, defaultValue: 90 },
                    { name: 'drugs', id: 12, defaultValue: 90 }
                ]
            },
            {
                name: 'offensive',
                label: T._('Hate and offensive', 'Admin'),
                subcategories: [{ name: 'offensive', id: 3, defaultValue: 90 }]
            },
            { name: 'celebrities', label: T._('Celebrities', 'Admin'), subcategories: [{ name: 'celebrities', id: 4, defaultValue: 92 }] },
            {
                name: 'text',
                label: T._('Images with texts', 'Admin'),
                subcategories: [
                    { name: 'artificialtext', id: 5, defaultValue: 82 },
                    { name: 'naturaltext', id: 16, defaultValue: 90 }
                ]
            },
            { name: 'ocr', label: T._('OCR', 'Admin'), subcategories: [{ name: 'ocr', id: 6, defaultValue: 1 }] },
            {
                name: 'faces',
                label: T._('Gender, minors, and faces', 'Admin'),
                subcategories: [
                    { name: 'minor', id: 7, defaultValue: 94 },
                    { name: 'female', id: 13, defaultValue: 92 },
                    { name: 'male', id: 14, defaultValue: 92 },
                    { name: 'sunglasses', id: 15, defaultValue: 95 }
                ]
            },
            { name: 'scam', label: T._('Scam', 'Admin'), subcategories: [{ name: 'scam', id: 8, defaultValue: 95 }] }
        ],
        []
    );

export const SUBCATEGORY_TO_CATEGORY: Record<string, string> = {
    nudity: 'nudity',
    nuditypartial: 'nudity',
    nuditysafe: 'nudity',
    weapon: 'wad',
    alcohol: 'wad',
    drugs: 'wad',
    offensive: 'offensive',
    celebrities: 'celebrities',
    artificialtext: 'text',
    naturaltext: 'text',
    ocr: 'ocr',
    minor: 'faces',
    female: 'faces',
    male: 'faces',
    sunglasses: 'faces',
    scam: 'scam'
};

export const useConditionOperators = () =>
    useMemo(
        () => [
            { value: '=', label: T._('= Equal', 'Admin'), connector: T._('to', 'Admin') },
            { value: '!=', label: T._('\u2260 Not equal', 'Admin'), connector: T._('to', 'Admin') },
            { value: '<', label: T._('< Less', 'Admin'), connector: T._('than', 'Admin') },
            { value: '<=', label: T._('\u2264 Less/equal', 'Admin'), connector: T._('than', 'Admin') },
            { value: '>', label: T._('> Greater', 'Admin'), connector: T._('than', 'Admin') },
            { value: '>=', label: T._('\u2265 Greater/equal', 'Admin'), connector: T._('than', 'Admin') }
        ],
        []
    );

export const FULL_MODE_ONLY_TOOLS = ['3d-preview', 'ai-lifestyle-scenes', 'ai-try-on', 'bulk-variations'];
export const BOOLEAN_TOOLS = ['shapes', '3d-preview', 'placement', 'ai-image-generation', 'ai-design-generation', 'bulk-variations', 'images'];

export const useActionTypeLabels = () =>
    useMemo<Record<number, string>>(
        () => ({
            99: T._('Show objects', 'Admin'),
            2: T._('Hide objects', 'Admin'),
            3: T._('Set object material', 'Admin'),
            7: T._('Set sub object material', 'Admin'),
            4: T._('Set material color', 'Admin'),
            5: T._('Move camera', 'Admin'),
            11: T._('Set design material', 'Admin'),
            8: T._('Set camera pivot', 'Admin'),
            9: T._('Reset camera pivot', 'Admin'),
            17: T._('Set material parameter', 'Admin')
        }),
        []
    );

export const ACTION_TYPE_ORDER = [99, 2, 3, 7, 4, 5, 11, 8, 9, 17];
export const OBJECT_ONLY_ACTION_TYPES = new Set([99, 2]);

export const ACTION_TYPE_COLORS: Record<number, { bg: string; fg: string }> = {
    99: { bg: '#d1fae5', fg: '#065f46' },
    2: { bg: '#fee2e2', fg: '#991b1b' },
    3: { bg: '#ede9fe', fg: '#7c3aed' },
    7: { bg: '#ede9fe', fg: '#7c3aed' },
    4: { bg: '#dbeafe', fg: '#1e40af' },
    5: { bg: '#fef3c7', fg: '#92400e' },
    11: { bg: '#fce7f3', fg: '#be185d' },
    8: { bg: '#fef3c7', fg: '#92400e' },
    9: { bg: '#fef3c7', fg: '#92400e' },
    17: { bg: '#e0e7ff', fg: '#3730a3' }
};

export const useMaterialColorTypes = () =>
    useMemo(
        () => [
            { label: T._('Diffuse', 'Admin'), value: 'diffuseColor' },
            { label: T._('Albedo', 'Admin'), value: 'albedoColor' },
            { label: T._('Base', 'Admin'), value: 'baseColor' },
            { label: T._('Ambient', 'Admin'), value: 'ambientColor' },
            { label: T._('Specular', 'Admin'), value: 'specularColor' },
            { label: T._('Emissive', 'Admin'), value: 'emissiveColor' },
            { label: T._('Reflection', 'Admin'), value: 'reflectionColor' }
        ],
        []
    );

export const MATERIAL_SELECTOR_ACTION_TYPES = new Set([4, 17]);
export const MATERIAL_VALUE_ACTION_TYPES = new Set([3, 7, 11]);
export const CAMERA_ACTION_TYPES = new Set([5, 8]);
export const NO_FIELDS_ACTION_TYPES = new Set([9]);

// ========== Helpers ==========

export const getToolLabel = (env: string) => {
    switch (env) {
        case 'CUSTOMIZER':
            return T._('Customizer', 'Admin');
        case 'COMPOSER':
            return T._('Configurator', 'Admin');
        case 'VIEWER':
            return T._('Viewer', 'Admin');
        default:
            return env;
    }
};

export const getSalesChannelCount = (product: SidePanelProduct) => {
    let count = 0;
    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
        count = 1;
    }
    count += product.additionalCodes.filter((ac) => ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE').length;
    return count;
};

export const getAllSalesChannels = (product: SidePanelProduct) => {
    const channels: { name: string; ecommerce: string }[] = [];
    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
        channels.push({
            name: product.salesChannel.detail?.properties?.name || product.salesChannel.ecommerceSettings?.ecommerceName || 'Unknown',
            ecommerce: product.salesChannel.ecommerceSettings?.ecommerceName || ''
        });
    }
    product.additionalCodes.forEach((ac) => {
        if (ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
            channels.push({
                name: ac.salesChannel.detail?.properties?.name || ac.salesChannel.ecommerceSettings?.ecommerceName || 'Unknown',
                ecommerce: ac.salesChannel.ecommerceSettings?.ecommerceName || ''
            });
        }
    });
    return channels;
};

export const getToolInfo = (
    product: SidePanelProduct,
    toolKey: string,
    toolData: ProductToolData
): { count: number; status: string | null; items: { id: string; name: string; previewUrl?: string | null }[] } => {
    switch (toolKey) {
        case 'printing-methods': {
            const nonDefaultMethods = (product.printingMethods || []).filter((pm) => pm.printTypeID?.toString() !== '1');
            return {
                count: nonDefaultMethods.length,
                status: null,
                items: nonDefaultMethods.map((pm) => ({ id: pm.id, name: pm.name }))
            };
        }
        case 'templates':
            return {
                count: toolData.templates?.length || 0,
                status: null,
                items: (toolData.templates || []).map((t) => ({ id: t.id, name: t.name || `#${t.id}`, previewUrl: t.previewUrl }))
            };
        case 'shapes':
            return {
                count: toolData.shapesConfigured ? 1 : 0,
                status: toolData.loaded ? (toolData.shapesConfigured ? T._('Configured', 'Admin') : null) : null,
                items: []
            };
        case 'pricing':
            return {
                count: toolData.pricingRules?.length || 0,
                status:
                    toolData.loaded && toolData.pricingRules?.length
                        ? `${toolData.pricingRules.length} ${toolData.pricingRules.length === 1 ? T._('rule', 'Admin') : T._('rules', 'Admin')}`
                        : null,
                items: toolData.pricingRules || []
            };
        case 'profanity':
            return {
                count: toolData.profanityRules?.length || 0,
                status: null,
                items: toolData.profanityRules || []
            };
        case 'ai-lifestyle-scenes':
            return {
                count: toolData.aiStagingRules?.length || 0,
                status:
                    toolData.loaded && toolData.aiStagingRules?.length
                        ? `${toolData.aiStagingRules.length} ${toolData.aiStagingRules.length === 1 ? T._('rule', 'Admin') : T._('rules', 'Admin')}`
                        : null,
                items: toolData.aiStagingRules || []
            };
        case 'ai-try-on':
            return {
                count: toolData.aiVirtualModelRules?.length || 0,
                status:
                    toolData.loaded && toolData.aiVirtualModelRules?.length
                        ? `${toolData.aiVirtualModelRules.length} ${toolData.aiVirtualModelRules.length === 1 ? T._('rule', 'Admin') : T._('rules', 'Admin')}`
                        : null,
                items: toolData.aiVirtualModelRules || []
            };
        case '3d-preview': {
            const has3D = toolData.has3DScene;
            const statusParts: string[] = [];
            if (has3D) statusParts.push(T._('Configured', 'Admin'));
            if (product.zakekeEnvironment === 'VIEWER') statusParts.push(T._('Viewer product', 'Admin'));
            return {
                count: has3D ? 1 : 0,
                status: toolData.loaded && statusParts.length > 0 ? statusParts.join(' \u00b7 ') : null,
                items: []
            };
        }
        case 'collage':
            return {
                count: toolData.collageRules?.length || 0,
                status: null,
                items: toolData.collageRules || []
            };
        case 'area-background':
            return {
                count: toolData.areaBackgroundRules?.length || 0,
                status: null,
                items: toolData.areaBackgroundRules || []
            };
        case 'get-a-quote':
            return {
                count: toolData.quoteRule ? 1 : 0,
                status: null,
                items: toolData.quoteRule ? [toolData.quoteRule] : []
            };
        case 'moq':
            return {
                count: toolData.moqRules?.length || 0,
                status: null,
                items: toolData.moqRules || []
            };
        case 'extra-options':
            return {
                count: toolData.extraOptions?.length || 0,
                status: null,
                items: toolData.extraOptions || []
            };
        case 'placement':
            return {
                count: toolData.placementConfigured ? 1 : 0,
                status: toolData.loaded ? (toolData.placementConfigured ? T._('Configured', 'Admin') : null) : null,
                items: []
            };
        case 'ai-image-generation':
            return {
                count: toolData.aiImageGenConfigured ? 1 : 0,
                status: toolData.loaded ? (toolData.aiImageGenConfigured ? T._('Configured', 'Admin') : null) : null,
                items: []
            };
        case 'ai-design-generation':
            return {
                count: toolData.aiDesignGenConfigured ? 1 : 0,
                status: toolData.loaded ? (toolData.aiDesignGenConfigured ? T._('Configured', 'Admin') : null) : null,
                items: []
            };
        case 'bulk-variations':
            return {
                count: toolData.bulkVariationEnabled ? 1 : 0,
                status: toolData.loaded ? (toolData.bulkVariationEnabled ? T._('Configured', 'Admin') : null) : null,
                items: []
            };
        case 'preset-configurations':
            return { count: 0, status: null, items: [] };
        case 'images': {
            const restrictions = toolData.imageRestrictions || [];
            const anyEnabled = restrictions.some((r) => r.clipartGalleries);
            return {
                count: anyEnabled ? 1 : 0,
                status: restrictions.length > 0 ? (anyEnabled ? T._('Enabled', 'Admin') : T._('Disabled', 'Admin')) : null,
                items: []
            };
        }
        default:
            return { count: 0, status: null, items: [] };
    }
};

export const parseFormula = (formula: string): { operator: string; values: string[] } => {
    try {
        const parsed = JSON.parse(formula);
        return { operator: parsed.operator || '=', values: parsed.values || [] };
    } catch {
        return { operator: '=', values: [] };
    }
};

export const buildFormula = (operator: string, values: string[]): string => {
    return JSON.stringify({ operator, values });
};
